import type { ButtonProps as NextButtonProps } from '@heroui/react';
import type { ActionIconProps } from '@mantine/core';

import {
  ArrowDownTrayIcon,
  CloudArrowDownIcon,
  PencilIcon,
  ShareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { extendVariants, Button as NextButton } from '@heroui/react';
import { ActionIcon } from '@mantine/core';
import { useTranslations } from 'next-intl';

interface ButtonProps extends Omit<NextButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'transparent' | 'cellRenderer';
}

export const Button = extendVariants(NextButton, {
  compoundVariants: [
    {
      class:
        'flex cursor-pointer select-none items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-sm border border-solid px-3 text-center font-medium uppercase outline-none focus:outline-none',
      variant: 'primary',
    },
    {
      class:
        'flex cursor-pointer select-none items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-sm border border-solid px-3 text-center font-medium uppercase outline-none focus:outline-none',
      variant: 'secondary',
    },
    {
      class:
        'flex cursor-pointer select-none items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-sm px-3 text-center font-medium uppercase outline-none focus:outline-none',
      variant: 'transparent',
    },
    {
      class:
        'flex cursor-pointer select-none items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-sm text-center font-medium uppercase outline-none focus:outline-none',
      variant: 'cellRenderer',
    },
    {
      class: 'border-gray-300 bg-gray-300 text-gray-400 ',
      isDisabled: true,
      variant: 'primary',
    },
    {
      class: 'border-gray-300 bg-gray-300 text-gray-400 ',
      isDisabled: true,
      variant: 'secondary',
    },
  ],
  defaultVariants: {
    size: 'sm',
    variant: 'primary',
  },
  variants: {
    isDisabled: {
      true: 'opacity-85 pointer-events-none',
    },
    size: {
      lg: 'h-9 px-5 text-sm',
      md: 'h-[34px] text-xs',
      sm: 'h-6 text-xs',
      xl: 'h-12 px-5 text-lg normal-case',
      iconButton: 'h-5 p-1 text-sm font-medium mx-[2px]',
    },
    variant: {
      cellRenderer:
        'text-orange-default hover:text-white active:text-white bg-transparent hover:bg-orange-default active:bg-orange-default inline-flex border-none py-0 px-0 min-w-0',
      primary:
        'text-white bg-orange-default border-orange-default hover:bg-orange-default/90 active:bg-orange-default/80',
      secondary:
        'text-orange-default bg-transparent border-orange-default hover:bg-orange-default/20 active:bg-orange-default/10',
      transparent: 'text-orange-default bg-transparent border-none',
      closeModal:
        'absolute appearance-none select-none top-1 end-1 p-2 text-foreground-500 rounded-full hover:bg-default-100 active:bg-default-200 tap-highlight-transparent outline-solid outline-transparent data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2',
    },
  },
});

type ActionButtonProps = ActionIconProps & { label?: string; onClick: () => void };

export const ActionButton = ({
  children,
  variant,
  size,
  color,
  label,
  onClick,
}: ActionButtonProps) => (
  <ActionIcon
    variant={variant}
    color={color ?? '#d04a02'}
    aria-label={label ?? 'action-button'}
    size={size}
    classNames={{
      root: '!rounded-none !size-4 !min-w-4 !min-h-4',
      icon: '!size-4 !min-w-4 !min-h-4',
    }}
    onClick={onClick}
  >
    {children}
  </ActionIcon>
);

export const CancelButton = (props: ButtonProps) => {
  const t = useTranslations();
  const { children = t('common.cancel'), ref, ...rest } = props;
  return (
    <Button
      ref={ref}
      startContent={
        <XMarkIcon
          height={20}
          width={20}
        />
      }
      variant="secondary"
      {...rest}
    >
      {children}
    </Button>
  );
};

export const DeleteButton = (props: ButtonProps) => {
  const t = useTranslations();
  const { children = t('common.contextMenu.delete'), ref, ...rest } = props;

  return (
    <Button
      ref={ref}
      startContent={
        <TrashIcon
          height={20}
          width={20}
        />
      }
      variant="primary"
      {...rest}
    >
      {children}
    </Button>
  );
};

export const SaveButton = (props: ButtonProps) => {
  const t = useTranslations();
  const { children = t('common.save'), ref, ...rest } = props;
  return (
    <Button
      ref={ref}
      startContent={
        <ArrowDownTrayIcon
          height={20}
          width={20}
        />
      }
      variant="primary"
      {...rest}
    >
      {children}
    </Button>
  );
};

export const EditButton = (props: ButtonProps) => {
  const t = useTranslations();
  const { children = t('common.contextMenu.edit'), ref, ...rest } = props;
  return (
    <Button
      ref={ref}
      startContent={
        <PencilIcon
          height={20}
          width={20}
        />
      }
      variant="primary"
      {...rest}
    >
      {children}
    </Button>
  );
};

export const ShareButton = (props: ButtonProps) => {
  const t = useTranslations();
  const { children = t('common.contextMenu.share'), ref, ...rest } = props;
  return (
    <Button
      ref={ref}
      startContent={
        <ShareIcon
          height={20}
          width={20}
        />
      }
      variant="primary"
      {...rest}
    >
      {children}
    </Button>
  );
};

export const ImportButton = (props: ButtonProps) => {
  const t = useTranslations();
  const { children = t('main.clients.menu.fileMenu.btnImport'), ref, ...rest } = props;
  return (
    <Button
      ref={ref}
      startContent={
        <CloudArrowDownIcon
          height={20}
          width={20}
        />
      }
      variant="primary"
      {...rest}
    >
      {children}
    </Button>
  );
};

export const CloseModalButton = (props: ButtonProps) => {
  const { ref, ...rest } = props;
  return (
    <Button
      aria-label="CloseModal"
      isIconOnly
      ref={ref}
      variant="closeModal"
      {...rest}
    >
      <XMarkIcon />
    </Button>
  );
};
